import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import vehicleService from '../../services/vehicleService.js';
import { Truck } from 'lucide-react';
import { PageLayout, Button } from '../common/index.js';

export default function AddVehicle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('vehicle');
  const [policyDocument, setPolicyDocument] = useState(null);
  const [policyDocumentPreview, setPolicyDocumentPreview] = useState(null);
  const [vehicleRC, setVehicleRC] = useState(null);
  const [vehicleRCPreview, setVehicleRCPreview] = useState(null);
  const [puc, setPuc] = useState(null);
  const [pucPreview, setPucPreview] = useState(null);
  const [purchaseInvoice, setPurchaseInvoice] = useState(null);
  const [purchaseInvoicePreview, setPurchaseInvoicePreview] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_name: '',
    vehicle_number: '',
    chassis_no: '',
    vehicle_type: 'cargo_goods',
    status: 'active',
    insurance_provider: '',
    agent_name: '',
    agent_no: '',
    policy_no: '',
    policy_start_date: '',
    policy_expiry_date: '',
    insured_declared_value: '',
    vehicle_contract_amount: ''
  });

  const vehicleTypes = [
    { value: 'cargo_goods', label: 'TRUCK' },
    { value: 'bus', label: 'BUS' }
  ];
  
  const vehicleStatuses = [
    { value: 'active', label: 'ACTIVE' },
    { value: 'inactive', label: 'INACTIVE' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePolicyDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Policy document must be less than 5MB');
        return;
      }
      setPolicyDocument(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPolicyDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePolicyDocument = () => {
    setPolicyDocument(null);
    setPolicyDocumentPreview(null);
    const fileInput = document.getElementById('policy-document-input');
    if (fileInput) fileInput.value = '';
  };

  const handleVehicleRCChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Vehicle RC must be less than 5MB');
        return;
      }
      setVehicleRC(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setVehicleRCPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeVehicleRC = () => {
    setVehicleRC(null);
    setVehicleRCPreview(null);
    const fileInput = document.getElementById('vehicle-rc-input');
    if (fileInput) fileInput.value = '';
  };

  const handlePucChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('PUC must be less than 5MB');
        return;
      }
      setPuc(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPucPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePuc = () => {
    setPuc(null);
    setPucPreview(null);
    const fileInput = document.getElementById('puc-input');
    if (fileInput) fileInput.value = '';
  };

  const handlePurchaseInvoiceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Purchase invoice must be less than 5MB');
        return;
      }
      setPurchaseInvoice(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPurchaseInvoicePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePurchaseInvoice = () => {
    setPurchaseInvoice(null);
    setPurchaseInvoicePreview(null);
    const fileInput = document.getElementById('purchase-invoice-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate date logic
    if (formData.policy_start_date && formData.policy_expiry_date) {
      if (new Date(formData.policy_start_date) > new Date(formData.policy_expiry_date)) {
        Alert.error('Policy start date cannot be after expiry date');
        return;
      }
    }
    
    try {
      setLoading(true);
      
      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      if (policyDocument) {
        formDataToSend.append('policy_document', policyDocument);
      }
      
      if (vehicleRC) {
        formDataToSend.append('vehicle_rc', vehicleRC);
      }
      
      if (puc) {
        formDataToSend.append('puc', puc);
      }
      
      if (purchaseInvoice) {
        formDataToSend.append('purchase_invoice', purchaseInvoice);
      }
      
      await vehicleService.createVehicle(formDataToSend);
      Alert.success('Vehicle created successfully');
      setTimeout(() => navigate('/vehicles'), 1000);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to create vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const hasData = formData.vehicle_name || formData.vehicle_number || 
                    formData.insurance_provider || policyDocument || 
                    vehicleRC || puc || purchaseInvoice;
    
    if (hasData) {
      const confirmed = await Alert.confirm(
        'You have unsaved changes. Are you sure you want to cancel?',
        'Unsaved Changes'
      );
      if (!confirmed) return;
    }
    
    navigate('/vehicles');
  };

  const tabClass = (tab) =>
    `pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
      activeTab === tab
        ? 'border-brand text-secondary'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  return (
    <PageLayout title="Add New Vehicle" icon={Truck} maxWidth="max-w-7xl">
      <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-card p-4 sm:p-8 space-y-6 sm:space-y-8">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button type="button" onClick={() => setActiveTab('vehicle')} className={tabClass('vehicle')}>
              Vehicle Information
            </button>
            <button type="button" onClick={() => setActiveTab('insurance')} className={tabClass('insurance')}>
              Insurance Details
            </button>
          </nav>
        </div>

                  {/* Vehicle Information Tab */}
                  {activeTab === 'vehicle' && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Vehicle Information
                      </h3>
                      <div className='grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6'>
                        {/* Vehicle Type */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Vehicle Type
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <select
                            name="vehicle_type"
                            value={formData.vehicle_type}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 text-tertiary rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          >
                            {vehicleTypes.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Vehicle Name */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Manufacturer
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <input
                            type="text"
                            name="vehicle_name"
                            value={formData.vehicle_name}
                            onChange={handleChange}
                            placeholder="Enter vehicle name (e.g., Tata Ace)"
                            required
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Vehicle Number */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Vehicle Number
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <input
                            type="text"
                            name="vehicle_number"
                            value={formData.vehicle_number}
                            onChange={handleChange}
                            placeholder="Enter vehicle number"
                            required
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        <div className="mt-0">
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Chassie No.
                              <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              type="text"
                              name="chassis_no"
                              value={formData.chassis_no}
                              onChange={handleChange}
                              placeholder="Enter chassis number"
                              required
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                            />
                          </div>

                        {/* Status */}
                        <div className='mt-6'>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Status
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full px-3 text-tertiary sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          >
                            {vehicleStatuses.map(status => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Contract Amount */}
                        <div className='mt-6'>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Contract Amount (₹)
                          </label>
                          <input
                            type="number"
                            name="vehicle_contract_amount"
                            value={formData.vehicle_contract_amount}
                            onChange={handleChange}
                            placeholder="Enter contract amount"
                            min="0"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>
                        </div>
                    </div>
                  )}

                  {/* Insurance Details Tab */}
                  {activeTab === 'insurance' && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Insurance Details
                      </h3>
                      <div className='grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6'>
                        {/* Insurance Provider */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Insurance Provider
                          </label>
                          <input
                            type="text"
                            name="insurance_provider"
                            value={formData.insurance_provider}
                            onChange={handleChange}
                            placeholder="Enter insurance provider"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Agent Name */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Agent Name
                          </label>
                          <input
                            type="text"
                            name="agent_name"
                            value={formData.agent_name}
                            onChange={handleChange}
                            placeholder="Enter agent name"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Agent Number */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Agent Number
                          </label>
                          <input
                            type="text"
                            name="agent_no"
                            value={formData.agent_no}
                            onChange={handleChange}
                            placeholder="Enter agent number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Policy Number */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Policy Number
                          </label>
                          <input
                            type="text"
                            name="policy_no"
                            value={formData.policy_no}
                            onChange={handleChange}
                            placeholder="Enter policy number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Policy Start Date */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Policy Start Date
                          </label>
                          <input
                            type="date"
                            name="policy_start_date"
                            value={formData.policy_start_date}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Policy Expiry Date */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Policy Expiry Date
                          </label>
                          <input
                            type="date"
                            name="policy_expiry_date"
                            value={formData.policy_expiry_date}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>

                        {/* Insured Declared Value */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Insured Declared Value (₹)
                          </label>
                          <input
                            type="number"
                            name="insured_declared_value"
                            value={formData.insured_declared_value}
                            onChange={handleChange}
                            placeholder="Enter IDV"
                            min="0"
                            step="0.01"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          />
                        </div>
                        {/* Policy Document Upload */}
                      <div className="mt-0">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Policy Document
                        </label>
                        <input
                          id="policy-document-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handlePolicyDocumentChange}
                          className="w-full px-3 sm:px-4 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload policy document (Images or PDF, Max 5MB)</p>
                        
                        {policyDocumentPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Document Preview:</p>
                            <div className="relative inline-block">
                              {policyDocument?.type === 'application/pdf' ? (
                                <div className="w-32 h-32 sm:w-40 sm:h-40 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                                  <div className="text-center">
                                    <svg className="w-12 h-12 mx-auto text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                    </svg>
                                    <p className="text-xs text-gray-600 mt-1">PDF File</p>
                                  </div>
                                </div>
                              ) : (
                                <img 
                                  src={policyDocumentPreview} 
                                  alt="Policy Document Preview" 
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                                />
                              )}
                              <button
                                type="button"
                                onClick={removePolicyDocument}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove document"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Vehicle RC Upload */}
                      <div className="mt-0">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Vehicle RC
                        </label>
                        <input
                          id="vehicle-rc-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleVehicleRCChange}
                          className="w-full px-3 sm:px-4 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload vehicle RC (Images or PDF, Max 5MB)</p>
                        
                        {vehicleRCPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Document Preview:</p>
                            <div className="relative inline-block">
                              {vehicleRC?.type === 'application/pdf' ? (
                                <div className="w-32 h-32 sm:w-40 sm:h-40 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                                  <div className="text-center">
                                    <svg className="w-12 h-12 mx-auto text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                    </svg>
                                    <p className="text-xs text-gray-600 mt-1">PDF File</p>
                                  </div>
                                </div>
                              ) : (
                                <img 
                                  src={vehicleRCPreview} 
                                  alt="Vehicle RC Preview" 
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                                />
                              )}
                              <button
                                type="button"
                                onClick={removeVehicleRC}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove document"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* PUC Upload */}
                      <div className="mt-0">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          PUC (Pollution Under Control)
                        </label>
                        <input
                          id="puc-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handlePucChange}
                          className="w-full px-3 sm:px-4 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload PUC (Images or PDF, Max 5MB)</p>
                        
                        {pucPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Document Preview:</p>
                            <div className="relative inline-block">
                              {puc?.type === 'application/pdf' ? (
                                <div className="w-32 h-32 sm:w-40 sm:h-40 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                                  <div className="text-center">
                                    <svg className="w-12 h-12 mx-auto text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                    </svg>
                                    <p className="text-xs text-gray-600 mt-1">PDF File</p>
                                  </div>
                                </div>
                              ) : (
                                <img 
                                  src={pucPreview} 
                                  alt="PUC Preview" 
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                                />
                              )}
                              <button
                                type="button"
                                onClick={removePuc}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove document"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Purchase Invoice Upload */}
                      <div className="mt-0">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Purchase Invoice
                        </label>
                        <input
                          id="purchase-invoice-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handlePurchaseInvoiceChange}
                          className="w-full px-3 sm:px-4 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload purchase invoice (Images or PDF, Max 5MB)</p>
                        
                        {purchaseInvoicePreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Document Preview:</p>
                            <div className="relative inline-block">
                              {purchaseInvoice?.type === 'application/pdf' ? (
                                <div className="w-32 h-32 sm:w-40 sm:h-40 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                                  <div className="text-center">
                                    <svg className="w-12 h-12 mx-auto text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                    </svg>
                                    <p className="text-xs text-gray-600 mt-1">PDF File</p>
                                  </div>
                                </div>
                              ) : (
                                <img 
                                  src={purchaseInvoicePreview} 
                                  alt="Purchase Invoice Preview" 
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                                />
                              )}
                              <button
                                type="button"
                                onClick={removePurchaseInvoice}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove document"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} className="sm:ml-auto">
            {loading ? 'Creating Vehicle...' : 'Create Vehicle'}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
}